import { useState, useCallback, useEffect } from 'react';
import { useMtlsOrganizations } from '../../hooks/useMtlsData';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import { ApiService } from '../../services/api';
import { 
  MtlsTable, 
  SearchFilter, 
  ConfirmationModal, 
  LoadingSpinner,
  StatusBadge 
} from './shared';
import { BulkOperations } from './shared/BulkOperations';
import { 
  handleApiError, 
  handleApiSuccess 
} from '../../utils/mtls-error-handler';
import { formatDateTime } from '../../utils/date-utils';
import { TableColumn } from '../../types/mtls';

interface Assignment {
  id: number;
  certificateId: number;
  environmentId: number;
  certificateName: string;
  environmentName: string;
  productName: string;
  organizationName: string;
  deploymentStatus: string;
  assignedAt: string;
  assignedBy: string;
  lastDeployment?: string;
  errorMessage?: string;
}

interface Environment {
  id: number;
  environmentName: string;
  productName: string;
  organizationName: string;
  environmentType: string;
  deploymentStatus: string;
  certificateCount: number;
}

export function AssignmentManagement(): JSX.Element {
  const [activeTab, setActiveTab] = useState<'assignments' | 'matrix' | 'environments' | 'pending'>('assignments');
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [pendingChanges, setPendingChanges] = useState<any[]>([]);
  const [availableCertificates, setAvailableCertificates] = useState<any[]>([]);
  const [availableEnvironments, setAvailableEnvironments] = useState<any[]>([]);
  const [assignmentMatrix, setAssignmentMatrix] = useState<any>(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Bulk selection for assignments
  const assignmentBulkSelection = useBulkSelection({
    items: assignments,
    getItemId: (assignment) => assignment.id,
  });

  // Bulk selection for environments
  const environmentBulkSelection = useBulkSelection({
    items: environments,
    getItemId: (environment) => environment.id,
  });
  
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  
  const [selectedCertificateId, setSelectedCertificateId] = useState<number>(0);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<number>(0);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number>(0);
  const [bulkAssignments, setBulkAssignments] = useState<Array<{certificateId: number; environmentId: number}>>([]);
  const [downloadingTrustStore, setDownloadingTrustStore] = useState<number | null>(null);
  
  const { organizations } = useMtlsOrganizations({ size: 100 });

  // Load data based on active tab
  useEffect(() => {
    loadData();
  }, [activeTab, selectedOrganizationId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('AssignmentManagement: Loading data for tab:', activeTab);
      console.log('AssignmentManagement: ApiService constructor name:', ApiService.constructor.name);
      
      if (activeTab === 'assignments') {
        const response = await ApiService.listMtlsAssignments();
        const raw: any[] = response.data || response.assignments || [];
        setAssignments(raw.map((a: any) => ({
          id: a.id,
          certificateId: a.certificateId,
          environmentId: a.productEnvironmentId ?? a.environmentId,
          certificateName: a.certificateName || a.commonName || a.common_name || `Certificate #${a.certificateId}`,
          environmentName: a.environmentName || a.environment_name || '',
          productName: a.productName || a.product_name || '',
          organizationName: a.organizationName || a.organization_name || '',
          deploymentStatus: a.deploymentStatus || a.status || '',
          assignedAt: a.assignedAt || a.createdAt || a.created_at || '',
          assignedBy: a.assignedBy || '',
          lastDeployment: a.lastDeployment || a.deploymentDate || undefined,
          errorMessage: a.errorMessage || a.deploymentError || undefined,
        })));
      } else if (activeTab === 'environments') {
        const response = await ApiService.getAvailableEnvironmentsForAssignment();
        setEnvironments(response.environments || []);
      } else if (activeTab === 'matrix') {
        const response = await ApiService.getAssignmentMatrix();
        setAssignmentMatrix(response);
      } else if (activeTab === 'pending') {
        const response = await ApiService.getPendingChanges();
        setPendingChanges(response.data || []);
      }
      
      // Load available certificates and environments for assignment operations
      const [certsResponse, envsResponse] = await Promise.all([
        ApiService.getAvailableCertificatesForAssignment(),
        ApiService.getAvailableEnvironmentsForAssignment(),
      ]);
      
      setAvailableCertificates(certsResponse.certificates || []);
      setAvailableEnvironments(envsResponse.environments || []);
      
    } catch (err: any) {
      console.error('AssignmentManagement: Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCertificate = async () => {
    setSelectedCertificateId(0);
    setSelectedEnvironmentId(0);
    setAvailableCertificates([]);
    setAvailableEnvironments([]);
    setShowAssignModal(true);
    try {
      const [certsResponse, envsResponse] = await Promise.all([
        ApiService.getAvailableCertificatesForAssignment(),
        ApiService.getAvailableEnvironmentsForAssignment(),
      ]);
      setAvailableCertificates(certsResponse.certificates || []);
      setAvailableEnvironments(envsResponse.environments || []);
    } catch (err) {
      handleApiError(err, 'Load Assignment Options');
    }
  };

  const handleUnassignCertificate = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setShowUnassignModal(true);
  };

  const handleBulkAssign = async () => {
    setBulkAssignments([]);
    setAvailableCertificates([]);
    setAvailableEnvironments([]);
    setShowBulkModal(true);
    try {
      const [certsResponse, envsResponse] = await Promise.all([
        ApiService.getAvailableCertificatesForAssignment(),
        ApiService.getAvailableEnvironmentsForAssignment(),
      ]);
      setAvailableCertificates(certsResponse.certificates || []);
      setAvailableEnvironments(envsResponse.environments || []);
    } catch (err) {
      handleApiError(err, 'Load Assignment Options');
    }
  };

  const handleSyncTrustStore = async (environmentId: number) => {
    setSubmitting(true);
    try {
      await ApiService.syncAllTrustStores();
      handleApiSuccess('Trust store synchronization initiated', 'Assignment Management');
      loadData();
    } catch (error) {
      handleApiError(error, 'Trust Store Sync');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadTrustStore = async (environmentId: number, environmentName: string) => {
    setDownloadingTrustStore(environmentId);
    try {
      const blob = await ApiService.downloadTrustStore(environmentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `truststore-${environmentName}-${new Date().toISOString().split('T')[0]}.jks`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      handleApiSuccess('Trust store downloaded successfully', 'Trust Store Download');
    } catch (error) {
      handleApiError(error, 'Trust Store Download');
    } finally {
      setDownloadingTrustStore(null);
    }
  };

  const handleAssignConfirm = async () => {
    if (selectedCertificateId === 0 || selectedEnvironmentId === 0) {
      handleApiError({ message: 'Please select both certificate and environment' }, 'Assignment');
      return;
    }

    setSubmitting(true);
    try {
      await ApiService.assignMtlsCertificate(selectedCertificateId, selectedEnvironmentId);
      handleApiSuccess('Certificate assigned successfully', 'Assignment Management');
      setShowAssignModal(false);
      loadData();
    } catch (error) {
      handleApiError(error, 'Certificate Assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassignConfirm = async () => {
    if (!selectedAssignment) return;

    setSubmitting(true);
    try {
      await ApiService.unassignMtlsCertificate(
        selectedAssignment.certificateId, 
        selectedAssignment.environmentId
      );
      handleApiSuccess('Certificate unassigned successfully', 'Assignment Management');
      setShowUnassignModal(false);
      loadData();
    } catch (error) {
      handleApiError(error, 'Certificate Unassignment');
    } finally {
      setSubmitting(false);
    }
  };

  // Bulk operations handlers
  const handleBulkUnassign = useCallback(async (assignments: Assignment[]) => {
    const progress = {
      total: assignments.length,
      completed: 0,
      failed: 0,
      errors: [] as Array<{ id: string; name: string; error: string }>,
    };

    for (const assignment of assignments) {
      try {
        await ApiService.unassignMtlsCertificate(assignment.certificateId, assignment.environmentId);
        progress.completed++;
      } catch (error: any) {
        progress.failed++;
        progress.errors.push({
          id: assignment.id.toString(),
          name: `${assignment.certificateName} → ${assignment.environmentName}`,
          error: error.message || 'Unassignment failed',
        });
      }
    }

    if (progress.completed > 0) {
      loadData();
    }

    return progress;
  }, []);

  const handleBulkDeploy = useCallback(async (assignments: Assignment[]) => {
    const progress = {
      total: assignments.length,
      completed: 0,
      failed: 0,
      errors: [] as Array<{ id: string; name: string; error: string }>,
    };

    for (const assignment of assignments) {
      try {
        await ApiService.deployMtlsCertificate(assignment.environmentId, assignment.certificateId);
        progress.completed++;
      } catch (error: any) {
        progress.failed++;
        progress.errors.push({
          id: assignment.id.toString(),
          name: `${assignment.certificateName} → ${assignment.environmentName}`,
          error: error.message || 'Deployment failed',
        });
      }
    }

    if (progress.completed > 0) {
      loadData();
    }

    return progress;
  }, []);

  const handleBulkSync = useCallback(async (environments: Environment[]) => {
    const progress = {
      total: environments.length,
      completed: 0,
      failed: 0,
      errors: [] as Array<{ id: string; name: string; error: string }>,
    };

    for (const environment of environments) {
      try {
        await ApiService.syncAllTrustStores();
        progress.completed++;
      } catch (error: any) {
        progress.failed++;
        progress.errors.push({
          id: environment.id.toString(),
          name: environment.environmentName,
          error: error.message || 'Sync failed',
        });
      }
    }

    if (progress.completed > 0) {
      loadData();
    }

    return progress;
  }, []);

  const assignmentBulkOperations = [
    {
      key: 'deploy',
      label: 'Deploy Selected',
      icon: 'fa-cloud-upload-alt',
      variant: 'primary' as const,
      requiresConfirmation: true,
      confirmationTitle: 'Bulk Certificate Deployment',
      confirmationMessage: 'This will deploy the selected certificate assignments to their environments. Continue?',
      onExecute: handleBulkDeploy,
    },
    {
      key: 'unassign',
      label: 'Unassign Selected',
      icon: 'fa-unlink',
      variant: 'danger' as const,
      requiresConfirmation: true,
      confirmationTitle: 'Bulk Certificate Unassignment',
      confirmationMessage: 'This will remove the selected certificate assignments. The certificates will no longer be available in these environments. Continue?',
      onExecute: handleBulkUnassign,
    },
  ];

  const environmentBulkOperations = [
    {
      key: 'sync',
      label: 'Sync Trust Stores',
      icon: 'fa-sync',
      variant: 'info' as const,
      requiresConfirmation: true,
      confirmationTitle: 'Bulk Trust Store Sync',
      confirmationMessage: 'This will synchronize the trust stores for the selected environments. Continue?',
      onExecute: handleBulkSync,
    },
  ];

  const handleBulkAssignConfirm = async () => {
    if (bulkAssignments.length === 0) {
      handleApiError({ message: 'Please add at least one assignment' }, 'Bulk Assignment');
      return;
    }

    setSubmitting(true);
    try {
      await ApiService.bulkAssignCertificates(bulkAssignments);
      handleApiSuccess(`${bulkAssignments.length} certificates assigned successfully`, 'Bulk Assignment');
      setShowBulkModal(false);
      loadData();
    } catch (error) {
      handleApiError(error, 'Bulk Assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const addBulkAssignment = () => {
    if (selectedCertificateId === 0 || selectedEnvironmentId === 0) return;
    
    const exists = bulkAssignments.some(
      a => a.certificateId === selectedCertificateId && a.environmentId === selectedEnvironmentId
    );
    
    if (!exists) {
      setBulkAssignments(prev => [...prev, {
        certificateId: selectedCertificateId,
        environmentId: selectedEnvironmentId
      }]);
      setSelectedCertificateId(0);
      setSelectedEnvironmentId(0);
    }
  };

  const removeBulkAssignment = (index: number) => {
    setBulkAssignments(prev => prev.filter((_, i) => i !== index));
  };

  const assignmentColumns: TableColumn<Assignment>[] = [
    {
      key: 'certificateName',
      title: 'Certificate',
      sortable: true,
      render: (assignment) => (
        <div>
          <strong>{assignment.certificateName}</strong>
          <div className="text-muted small">
            <i className="fas fa-building mr-1"></i>
            {assignment.organizationName}
          </div>
        </div>
      ),
    },
    {
      key: 'environmentName',
      title: 'Environment',
      sortable: true,
      render: (assignment) => (
        <div>
          <strong>{assignment.environmentName}</strong>
          <div className="text-muted small">
            <i className="fas fa-cube mr-1"></i>
            {assignment.productName}
          </div>
        </div>
      ),
    },
    {
      key: 'deploymentStatus',
      title: 'Deployment Status',
      render: (assignment) => (
        <StatusBadge 
          status={assignment.deploymentStatus} 
          type="deployment" 
        />
      ),
    },
    {
      key: 'assignedAt',
      title: 'Assigned',
      responsive: true,
      render: (assignment) => (
        <div>
          <div>{formatDateTime(assignment.assignedAt)}</div>
          <div className="text-muted small">by {assignment.assignedBy}</div>
        </div>
      ),
    },
    {
      key: 'lastDeployment',
      title: 'Last Deployment',
      responsive: true,
      render: (assignment) => assignment.lastDeployment ? formatDateTime(assignment.lastDeployment) : '-',
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (assignment) => (
        <div className="btn-group btn-group-sm">
          <button
            className="btn btn-outline-success"
            onClick={() => handleDownloadTrustStore(assignment.environmentId, assignment.environmentName)}
            title="Download Trust Store"
            disabled={downloadingTrustStore === assignment.environmentId}
          >
            {downloadingTrustStore === assignment.environmentId ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              <i className="fas fa-download"></i>
            )}
          </button>
          <button
            className="btn btn-outline-warning"
            onClick={() => handleSyncTrustStore(assignment.environmentId)}
            title="Sync Trust Store"
            disabled={submitting}
          >
            <i className="fas fa-sync"></i>
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={() => handleUnassignCertificate(assignment)}
            title="Unassign Certificate"
            style={{ display: undefined }}
          >
            <i className="fas fa-unlink"></i>
          </button>
        </div>
      ),
    },
  ];

  const environmentColumns: TableColumn<Environment>[] = [
    {
      key: 'environmentName',
      title: 'Environment',
      sortable: true,
      render: (env) => (
        <div>
          <strong>{env.environmentName}</strong>
          <div className="text-muted small">
            <i className="fas fa-cube mr-1"></i>
            {env.productName}
          </div>
        </div>
      ),
    },
    {
      key: 'organizationName',
      title: 'Organization',
      responsive: true,
    },
    {
      key: 'environmentType',
      title: 'Type',
      render: (env) => (
        <StatusBadge 
          status={env.environmentType} 
          type="environment" 
        />
      ),
    },
    {
      key: 'certificateCount',
      title: 'Certificates',
      render: (env) => (
        <span className="badge badge-info">
          {env.certificateCount}
        </span>
      ),
    },
    {
      key: 'deploymentStatus',
      title: 'Deployment Status',
      render: (env) => (
        <StatusBadge 
          status={env.deploymentStatus} 
          type="deployment" 
        />
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (env) => (
        <div className="btn-group btn-group-sm">
          <button
            className="btn btn-outline-success"
            onClick={() => handleDownloadTrustStore(env.id, env.environmentName)}
            title="Download Trust Store"
            disabled={downloadingTrustStore === env.id}
          >
            {downloadingTrustStore === env.id ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              <i className="fas fa-download"></i>
            )}
          </button>
          <button
            className="btn btn-outline-warning"
            onClick={() => handleSyncTrustStore(env.id)}
            title="Sync Trust Store"
            disabled={submitting}
          >
            <i className="fas fa-sync"></i>
          </button>
        </div>
      ),
    },
  ];

  const organizationFilterOptions = [
    {
      key: 'organizationId',
      label: 'Filter by Organization',
      options: organizations.map(org => ({
        value: org.id.toString(),
        label: org.name,
      })),
    },
  ];

  if (error) {
    return (
      <div className="alert alert-danger">
        <h4><i className="icon fas fa-ban"></i> Error!</h4>
        Failed to load assignment data: {error}
        <div className="mt-2">
          <button className="btn btn-outline-danger" onClick={loadData}>
            <i className="fas fa-redo mr-2"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="assignment-management">
      {/* Header */}
      <div className="row mb-3">
        <div className="col-sm-6">
          <h3>
            <i className="fas fa-link mr-2"></i>
            Certificate Assignments
          </h3>
        </div>
        <div className="col-sm-6 text-right">
          <div className="btn-group">
            <button
              className="btn btn-primary"
              onClick={handleAssignCertificate}
            >
              <i className="fas fa-plus mr-2"></i>
              Assign Certificate
            </button>
            <button
              className="btn btn-info"
              onClick={handleBulkAssign}
            >
              <i className="fas fa-layer-group mr-2"></i>
              Bulk Assign
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card card-primary card-tabs">
        <div className="card-header p-0 pt-1">
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === 'assignments' ? 'active' : ''}`}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('assignments');
                }}
              >
                <i className="fas fa-link mr-2"></i>
                Assignments
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === 'matrix' ? 'active' : ''}`}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('matrix');
                }}
              >
                <i className="fas fa-th mr-2"></i>
                Assignment Matrix
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === 'environments' ? 'active' : ''}`}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('environments');
                }}
              >
                <i className="fas fa-server mr-2"></i>
                Environments
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === 'pending' ? 'active' : ''}`}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('pending');
                }}
              >
                <i className="fas fa-exclamation-triangle mr-2"></i>
                Pending Changes
                {pendingChanges.length > 0 && (
                  <span className="badge badge-warning ml-1">{pendingChanges.length}</span>
                )}
              </a>
            </li>
          </ul>
        </div>
        <div className="card-body">
          {/* Search and Filters */}
          <div className="mb-3">
            <SearchFilter
              searchPlaceholder={
                activeTab === 'assignments' 
                  ? "Search assignments..." 
                  : activeTab === 'matrix'
                  ? "Search certificate matrix..."
                  : "Search environments..."
              }
              filters={organizationFilterOptions}
              onFilterChange={(key, value) => {
                if (key === 'organizationId') {
                  setSelectedOrganizationId(value === '' ? 0 : parseInt(value));
                }
              }}
            />
          </div>

          {/* Bulk Operations */}
          {activeTab === 'assignments' && (
            <BulkOperations
              selectedItems={assignmentBulkSelection.selectedItems}
              onClearSelection={assignmentBulkSelection.clearSelection}
              operations={assignmentBulkOperations}
            />
          )}

          {activeTab === 'environments' && (
            <BulkOperations
              selectedItems={environmentBulkSelection.selectedItems}
              onClearSelection={environmentBulkSelection.clearSelection}
              operations={environmentBulkOperations}
            />
          )}

          {/* Content based on active tab */}
          {activeTab === 'assignments' && (
            <MtlsTable
              data={assignments}
              columns={assignmentColumns}
              loading={loading}
              emptyMessage="No certificate assignments found. Assign certificates to environments to get started."
              bulkSelection={{
                enabled: true,
                selectedItems: assignmentBulkSelection.selectedItems,
                onToggleItem: assignmentBulkSelection.toggleItem,
                onToggleAll: assignmentBulkSelection.toggleAll,
                isSelected: assignmentBulkSelection.isSelected,
                isAllSelected: assignmentBulkSelection.isAllSelected,
                isPartiallySelected: assignmentBulkSelection.isPartiallySelected,
                getItemId: (assignment) => assignment.id,
              }}
            />
          )}

          {activeTab === 'matrix' && (
            <div className="assignment-matrix">
              {loading ? (
                <LoadingSpinner />
              ) : assignmentMatrix ? (
                <>
                  {/* Summary Cards */}
                  <div className="row mb-4">
                    <div className="col-md-3">
                      <div className="info-box">
                        <span className="info-box-icon bg-info">
                          <i className="fas fa-certificate"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Total Certificates</span>
                          <span className="info-box-number">{assignmentMatrix.summary?.totalCertificates || 0}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="info-box">
                        <span className="info-box-icon bg-success">
                          <i className="fas fa-server"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Total Environments</span>
                          <span className="info-box-number">{assignmentMatrix.summary?.totalEnvironments || 0}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="info-box">
                        <span className="info-box-icon bg-warning">
                          <i className="fas fa-link"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Total Assignments</span>
                          <span className="info-box-number">{assignmentMatrix.summary?.totalAssignments || 0}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="info-box">
                        <span className="info-box-icon bg-danger">
                          <i className="fas fa-exclamation-triangle"></i>
                        </span>
                        <div className="info-box-content">
                          <span className="info-box-text">Unassigned Certs</span>
                          <span className="info-box-number">{assignmentMatrix.summary?.unassignedCertificates || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Matrix Table */}
                  <div className="card">
                    <div className="card-header">
                      <h6 className="card-title">Certificate Assignment Matrix</h6>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                          <thead>
                            <tr>
                              <th>Certificate</th>
                              <th>Type</th>
                              <th>Organization</th>
                              <th>Assignments</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignmentMatrix.matrix?.map((row: any, index: number) => (
                              <tr key={index}>
                                <td>
                                  <div>
                                    <strong>{row.certificate.commonName}</strong>
                                    <div className="text-muted small">
                                      Expires: {formatDateTime(row.certificate.expiryDate)}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className={`badge badge-${row.certificate.certificateType === 'prod' ? 'success' : 'info'}`}>
                                    {row.certificate.certificateType === 'prod' ? 'Production' : 'Non-Production'}
                                  </span>
                                </td>
                                <td>{row.certificate.organizationName}</td>
                                <td>
                                  {row.assignments.length === 0 ? (
                                    <span className="text-muted">No assignments</span>
                                  ) : (
                                    <div>
                                      {row.assignments.map((assignment: any, idx: number) => (
                                        <div key={idx} className="mb-1">
                                          <span className="badge badge-outline-primary mr-1">
                                            {assignment.environmentName} ({assignment.environmentType})
                                          </span>
                                          <StatusBadge 
                                            status={assignment.deploymentStatus} 
                                            type="deployment" 
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <StatusBadge 
                                    status={row.certificate.status} 
                                    type="certificate" 
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted">
                  <i className="fas fa-th fa-3x mb-3"></i>
                  <p>No assignment matrix data available</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'environments' && (
            <MtlsTable
              data={environments}
              columns={environmentColumns}
              loading={loading}
              emptyMessage="No environments found. Create products with environments first."
              bulkSelection={{
                enabled: true,
                selectedItems: environmentBulkSelection.selectedItems,
                onToggleItem: environmentBulkSelection.toggleItem,
                onToggleAll: environmentBulkSelection.toggleAll,
                isSelected: environmentBulkSelection.isSelected,
                isAllSelected: environmentBulkSelection.isAllSelected,
                isPartiallySelected: environmentBulkSelection.isPartiallySelected,
                getItemId: (environment) => environment.id,
              }}
            />
          )}
        </div>
      </div>

      {/* Assign Certificate Modal */}
      {showAssignModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    <i className="fas fa-link mr-2"></i>
                    Assign Certificate
                  </h4>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setShowAssignModal(false)}
                    disabled={submitting}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="certificateSelect">Certificate *</label>
                        <select
                          className="form-control"
                          id="certificateSelect"
                          value={selectedCertificateId}
                          onChange={(e) => setSelectedCertificateId(parseInt(e.target.value))}
                          disabled={submitting}
                          required
                        >
                          <option value="">Select Certificate</option>
                          {availableCertificates.map(cert => (
                            <option key={cert.id} value={cert.id}>
                              {cert.commonName} ({cert.organizationName})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="environmentSelect">Environment *</label>
                        <select
                          className="form-control"
                          id="environmentSelect"
                          value={selectedEnvironmentId}
                          onChange={(e) => setSelectedEnvironmentId(parseInt(e.target.value))}
                          disabled={submitting}
                          required
                        >
                          <option value="">Select Environment</option>
                          {availableEnvironments.map(env => (
                            <option key={env.id} value={env.id}>
                              {env.environmentName} ({env.productName})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer justify-content-between">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => setShowAssignModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAssignConfirm}
                    disabled={submitting || selectedCertificateId === 0 || selectedEnvironmentId === 0}
                  >
                    {submitting && (
                      <span className="spinner-border spinner-border-sm mr-2" role="status">
                        <span className="sr-only">Loading...</span>
                      </span>
                    )}
                    Assign Certificate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bulk Assignment Modal */}
      {showBulkModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    <i className="fas fa-layer-group mr-2"></i>
                    Bulk Certificate Assignment
                  </h4>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setShowBulkModal(false)}
                    disabled={submitting}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {/* Add Assignment Form */}
                  <div className="card card-outline card-info mb-3">
                    <div className="card-header">
                      <h6 className="card-title">Add Assignment</h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-5">
                          <select
                            className="form-control"
                            value={selectedCertificateId}
                            onChange={(e) => setSelectedCertificateId(parseInt(e.target.value))}
                            disabled={submitting}
                          >
                            <option value="">Select Certificate</option>
                            {availableCertificates.map(cert => (
                              <option key={cert.id} value={cert.id}>
                                {cert.commonName} ({cert.organizationName})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-5">
                          <select
                            className="form-control"
                            value={selectedEnvironmentId}
                            onChange={(e) => setSelectedEnvironmentId(parseInt(e.target.value))}
                            disabled={submitting}
                          >
                            <option value="">Select Environment</option>
                            {availableEnvironments.map(env => (
                              <option key={env.id} value={env.id}>
                                {env.environmentName} ({env.productName})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-2">
                          <button
                            className="btn btn-info btn-block"
                            onClick={addBulkAssignment}
                            disabled={selectedCertificateId === 0 || selectedEnvironmentId === 0}
                          >
                            <i className="fas fa-plus"></i> Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Assignment List */}
                  <div className="card card-outline card-primary">
                    <div className="card-header">
                      <h6 className="card-title">Assignments to Process ({bulkAssignments.length})</h6>
                    </div>
                    <div className="card-body">
                      {bulkAssignments.length === 0 ? (
                        <p className="text-muted text-center">No assignments added yet.</p>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Certificate</th>
                                <th>Environment</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bulkAssignments.map((assignment, index) => {
                                const cert = availableCertificates.find(c => c.id === assignment.certificateId);
                                const env = availableEnvironments.find(e => e.id === assignment.environmentId);
                                return (
                                  <tr key={index}>
                                    <td>{cert?.commonName} ({cert?.organizationName})</td>
                                    <td>{env?.environmentName} ({env?.productName})</td>
                                    <td>
                                      <button
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => removeBulkAssignment(index)}
                                      >
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer justify-content-between">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => setShowBulkModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleBulkAssignConfirm}
                    disabled={submitting || bulkAssignments.length === 0}
                  >
                    {submitting && (
                      <span className="spinner-border spinner-border-sm mr-2" role="status">
                        <span className="sr-only">Loading...</span>
                      </span>
                    )}
                    Process {bulkAssignments.length} Assignments
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Unassign Confirmation Modal */}
      {activeTab === 'pending' && (
        <div className="pending-changes">
          {pendingChanges.length === 0 ? (
            <div className="alert alert-success">
              <i className="fas fa-check-circle mr-2"></i>
              All environments are in sync — no pending changes.
            </div>
          ) : (
            pendingChanges.map((env: any) => (
              <div key={env.productEnvironmentId} className="card mb-3">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="fas fa-exclamation-triangle text-warning mr-2"></i>
                    {env.environmentName}
                    <span className="text-muted ml-2 small">({env.productName} — {env.organizationName})</span>
                    <span className="badge badge-secondary ml-2">{env.environmentType}</span>
                  </h5>
                </div>
                <div className="card-body">
                  {env.pendingAdditions.length > 0 && (
                    <div className="mb-2">
                      <strong className="text-success"><i className="fas fa-plus-circle mr-1"></i>Pending Additions</strong>
                      <ul className="list-unstyled mt-1 ml-3">
                        {env.pendingAdditions.map((c: any) => (
                          <li key={c.id}>
                            <span className="badge badge-success mr-2">ADD</span>
                            {c.certificateName}
                            <span className="text-muted small ml-2">requested by {c.requestedBy} at {new Date(c.requestedAt).toLocaleString()}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {env.pendingRemovals.length > 0 && (
                    <div>
                      <strong className="text-danger"><i className="fas fa-minus-circle mr-1"></i>Pending Removals</strong>
                      <small className="text-muted ml-2">— cert still active in trust store until deployed</small>
                      <ul className="list-unstyled mt-1 ml-3">
                        {env.pendingRemovals.map((c: any) => (
                          <li key={c.id}>
                            <span className="badge badge-danger mr-2">REMOVE</span>
                            {c.certificateName}
                            <span className="text-muted small ml-2">requested by {c.requestedBy} at {new Date(c.requestedAt).toLocaleString()}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Unassign Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnassignModal}
        title="Unassign Certificate"
        message={`Are you sure you want to unassign "${selectedAssignment?.certificateName}" from "${selectedAssignment?.environmentName}"? This will remove the certificate from the trust store.`}
        confirmText="Unassign"
        confirmVariant="danger"
        onConfirm={handleUnassignConfirm}
        onCancel={() => setShowUnassignModal(false)}
        loading={submitting}
      />
    </div>
  );
}